/**
 * Malware scan gate state → UI badge.
 *
 * The backend returns `scan_status` on each document:
 *   "pending"         — held for the async ClamAV scan (gate hasn't released it yet)
 *   "clean"           — scanned clean (normal status badge applies)
 *   "clean_unscanned" — released WITHOUT scanning (oversize skip); badged so an
 *                       unscanned file is never mistaken for a scanned-clean one
 *   "infected"        — quarantined; the doc is blocked, NOT a generic failure
 *   "error"           — scan errored (rare; falls through to normal status)
 *
 * When this returns a badge it should OVERRIDE the normal status badge:
 *   - "pending" supersedes the plain "Pending" so users know it's being scanned
 *   - "infected" supersedes "Failed" so a blocked file reads as malware, not a bug
 *   - "clean_unscanned" supersedes "Completed" — the skip must stay visible
 *
 * Pure leaf module (no SPA imports) so it can be used from any surface without
 * risking a circular-import TDZ blank screen.
 */
export function scanBadge(doc) {
  const s = doc && doc.scan_status;
  if (s === 'pending') {
    return {
      state: 'scanning',
      label: 'Scanning…',
      longLabel: 'Scanning for malware…',
      title: 'Held while we scan this file for malware before processing.',
      invCls: 'inv-status--scanning',
      amCls: 'am-badge-scanning',
    };
  }
  if (s === 'infected') {
    return {
      state: 'infected',
      label: 'Malware blocked',
      longLabel: 'Rejected — malware detected',
      title: 'This file was flagged as malware and quarantined. It will not be processed.',
      invCls: 'inv-status--infected',
      amCls: 'am-badge-infected',
    };
  }
  if (s === 'clean_unscanned') {
    return {
      state: 'unscanned',
      label: 'Not virus-scanned',
      longLabel: 'Released without malware scan (file too large)',
      title: 'This file exceeds the malware scanner’s size limit and was released without being scanned.',
      invCls: 'inv-status--unscanned',
      amCls: 'am-badge-unscanned',
    };
  }
  return null;
}
